/**
 * Speedjunkies Giveaway Console Component
 * Fetches latest active giveaways & current multiplier dynamically.
 * Features:
 *  - Dynamic JSON endpoint integration with fallback to Liquid SSR
 *  - Fully accessible WAI-ARIA and WCAG 2.2 compliant slider/slideshow
 *  - Automated cross-fade transitions with mouse/keyboard/focus pause controls
 *  - Media-rich slideshow support (Images and Videos)
 *  - Client-side dynamic Entries calculation on product cards matching mockup style
 */

class GiveawayConsole extends HTMLElement {
  constructor() {
    super();
    this.container = null;
    this.slides = [];
    this.dots = [];
    this.currentIndex = 0;
    this.autoplayTimer = null;
    this.autoplayDelay = 6000; // 6s interval for premium pacing
  }

  async connectedCallback() {
    this.container = this.querySelector('#giveaway-slideshow-container');
    
    // Initialize top-right header dropdowns for giveaway entries selection
    this.initHeaderDropdowns();
    
    // Always store a default/fallback multiplier on window if not yet set
    window.currentGiveawayMultiplier = window.currentGiveawayMultiplier || 10;

    // Listen for variant updates globally to recalculate entries and multiplier badges on product pages
    this.onVariantUpdate = () => {
      setTimeout(() => {
        if (window.currentGiveawayMultiplier) {
          this.updateGlobalProductEntries(window.currentGiveawayMultiplier);
        }
      }, 50); // Minor tick to ensure DOM is swapped by theme price components
    };
    document.addEventListener('variant:update', this.onVariantUpdate);

    // Run initial entries refresh on load with whatever is the currently stored multiplier
    this.updateGlobalProductEntries(window.currentGiveawayMultiplier);

    if (!this.container) return;

    try {
      // 1. Fetch current entry multiplier from API
      let multiplier = window.currentGiveawayMultiplier;
      try {
        const multRes = await fetch('/apps/api/current-multiplier');
        if (multRes.ok) {
          const multData = await multRes.json();
          if (multData && multData.success) {
            multiplier = multData.totalMultiplier || multData.baseMultiplier || 10;
            window.currentGiveawayMultiplier = multiplier;
            // Update any dynamic multiplier badges/headers in the console header
            this.updateMultiplierUI(multData);
            // Update all product cards with new entries
            this.updateGlobalProductEntries(multiplier);
          }
        }
      } catch (err) {
        console.error('[GiveawayConsole] Failed to fetch current multiplier:', err);
      }

      // 2. Fetch latest active giveaways from API
      const giveawayRes = await fetch('/apps/api/latest-giveaway');
      if (giveawayRes.ok) {
        const giveawayData = await giveawayRes.json();
        
        let giveaways = [];
        // 1. If latestGiveaway is present, combine latestGiveaway and activeGiveaways (legacy style)
        if (giveawayData && giveawayData.latestGiveaway) {
          giveaways = [giveawayData.latestGiveaway];
          if (giveawayData.activeGiveaways && Array.isArray(giveawayData.activeGiveaways)) {
            giveaways.push(...giveawayData.activeGiveaways);
          }
          
          // Dynamically extract multiplier from latestGiveaway
          if (giveawayData.latestGiveaway.effective_multiplier) {
            multiplier = giveawayData.latestGiveaway.effective_multiplier;
            window.currentGiveawayMultiplier = multiplier;
            this.updateGlobalProductEntries(multiplier);
          }
        }
        // 2. Otherwise, if only activeGiveaways is present, use activeGiveaways directly (new style)
        else if (giveawayData && giveawayData.activeGiveaways && Array.isArray(giveawayData.activeGiveaways)) {
          giveaways = giveawayData.activeGiveaways;
          
          // Dynamically extract the multiplier from stats object if available
          if (giveawayData.stats && giveawayData.stats.currentMultiplier) {
            const parsedMult = parseFloat(giveawayData.stats.currentMultiplier);
            if (!isNaN(parsedMult)) {
              multiplier = parsedMult;
              window.currentGiveawayMultiplier = multiplier;
              this.updateGlobalProductEntries(multiplier);
            }
          }
        }

        if (giveaways.length > 0) {
          this.renderGiveaways(giveaways, multiplier);
        } else {
          console.warn('[GiveawayConsole] API succeeded but returned incomplete data. Using SSR fallback.');
        }
      } else {
        console.warn('[GiveawayConsole] API endpoint returned non-OK status. Using SSR fallback.');
      }
    } catch (error) {
      console.error('[GiveawayConsole] Critical initialization error. Fallback to SSR.', error);
    }
  }

  disconnectedCallback() {
    this.stopAutoplay();
    if (this.onVariantUpdate) {
      document.removeEventListener('variant:update', this.onVariantUpdate);
    }
  }

  /**
   * Initialize giveaway selector and customer entries dropdown in the terminal header
   */
  async initHeaderDropdowns() {
    const giveawaySelect = this.querySelector('#header-giveaway-select');
    const entriesDropdown = this.querySelector('#header-entries-dropdown');
    const customerId = this.getAttribute('data-customer-id');

    if (!giveawaySelect || !entriesDropdown) return;

    try {
      // 1. Fetch giveaways to populate the selector
      const response = await fetch('/apps/api/giveaway-entries');
      if (response.ok) {
        const data = await response.json();
        if (data && Array.isArray(data.giveaways) && data.giveaways.length > 0) {
          // Clear current loading option
          giveawaySelect.innerHTML = '';
          
          data.giveaways.forEach(giveaway => {
            const option = document.createElement('option');
            option.value = giveaway.id;
            option.textContent = giveaway.title.toUpperCase();
            giveawaySelect.appendChild(option);
          });
          
          // Add change listener to fetch entries for the selected giveaway
          giveawaySelect.addEventListener('change', async () => {
            const selectedGiveawayId = giveawaySelect.value;
            if (!selectedGiveawayId) {
              entriesDropdown.textContent = customerId 
                ? '0 ENTRIES' 
                : 'LOG IN FOR ENTRIES';
              return;
            }

            if (!customerId) {
              entriesDropdown.textContent = 'LOG IN FOR ENTRIES';
              return;
            }

            // Fetch entries count for selected giveaway
            entriesDropdown.textContent = 'FETCHING...';
            try {
              const entriesRes = await fetch(`/apps/api/giveaway-entries?giveawayId=${selectedGiveawayId}&customerId=${customerId}`);
              if (entriesRes.ok) {
                const entriesData = await entriesRes.json();
                if (entriesData && entriesData.my_entries) {
                  const myEntries = entriesData.my_entries;
                  const total = myEntries.corrected_total_entries || 0;
                  const pending = myEntries.pending_entries || 0;
                  const sum = total + pending;
                  
                  entriesDropdown.textContent = `${sum} ENTRIES`;
                } else {
                  entriesDropdown.textContent = '0 ENTRIES';
                }
              } else {
                entriesDropdown.textContent = '0 ENTRIES';
              }
            } catch (err) {
              console.error('[GiveawayConsole] Error fetching customer entries:', err);
              entriesDropdown.textContent = '0 ENTRIES';
            }
          });

          // Set latest active giveaway as default selected
          giveawaySelect.selectedIndex = 0;
          // Trigger change event to fetch entries automatically
          giveawaySelect.dispatchEvent(new Event('change'));
        } else {
          giveawaySelect.innerHTML = '<option value="">NO GIVEAWAYS</option>';
          entriesDropdown.textContent = '0 ENTRIES';
        }
      } else {
        giveawaySelect.innerHTML = '<option value="">ERROR LOADING</option>';
        entriesDropdown.textContent = '0 ENTRIES';
      }
    } catch (err) {
      console.error('[GiveawayConsole] Failed to initialize header dropdowns:', err);
      giveawaySelect.innerHTML = '<option value="">ERROR LOADING</option>';
      entriesDropdown.innerHTML = '<option value="">0 ENTRIES</option>';
    }
  }

  /**
   * Update all dynamic product card entry labels on the page
   */
  updateGlobalProductEntries(multiplier) {
    if (typeof window.updateGlobalProductEntries === 'function') {
      window.updateGlobalProductEntries(multiplier);
      return;
    }

    const labels = document.querySelectorAll('[data-product-entries-label]');
    labels.forEach(label => {
      const priceDollars = parseFloat(label.getAttribute('data-price-dollars'));
      if (!isNaN(priceDollars)) {
        const calculatedEntries = Math.round(priceDollars * multiplier);
        
        // Find existing text structure or replace
        label.innerHTML = `+${calculatedEntries.toLocaleString()} ENTRIES`;
      }
    });

    // Update and reveal all dynamic multiplier badges (product cards + product pages)
    const badges = document.querySelectorAll('[data-multiplier-badge]');
    badges.forEach(badge => {
      badge.textContent = `${multiplier}X`;
      badge.classList.remove('hidden');
      badge.style.display = 'inline-flex';
    });
  }

  /**
   * Update multiplier text in terminal headers if needed
   */
  updateMultiplierUI(multData) {
    const labelText = multData.label || `${multData.totalMultiplier || 10}X`;
    const headerStatus = document.getElementById('console-terminal-status');
    if (headerStatus) {
      headerStatus.innerHTML = `<span class="text-accent font-black">${labelText} LIVE</span> // STATUS: ONLINE`;
    }
  }

  /**
   * Helper to extract media sources (image and video URLs) from giveaway object supporting old and new structures
   */
  getMedia(giveaway) {
    let imageUrl = '';
    let videoUrl = '';

    // 1. Try to extract direct videoUrl / video_url from root
    videoUrl = giveaway.videoUrl || giveaway.video_url || '';

    // 2. Try to extract direct videoPreviewUrl / video_preview_url or imageUrl / image_url / image from root
    imageUrl = giveaway.videoPreviewUrl || giveaway.video_preview_url || giveaway.imageUrl || giveaway.image_url || giveaway.image || '';

    // 3. Inspect images array if present
    if (giveaway.images && Array.isArray(giveaway.images) && giveaway.images.length > 0) {
      // Find the first video in the images array if we don't have a videoUrl yet
      if (!videoUrl) {
        const videoImg = giveaway.images.find(img => img.type === 'VIDEO');
        if (videoImg) {
          videoUrl = videoImg.url || '';
          if (!imageUrl) {
            imageUrl = videoImg.previewUrl || videoImg.preview_url || '';
          }
        }
      }

      // If we still don't have imageUrl, look for the first IMAGE type or fallback to the first element's url/previewUrl
      if (!imageUrl) {
        const firstImg = giveaway.images.find(img => img.type === 'IMAGE') || giveaway.images[0];
        if (firstImg) {
          imageUrl = firstImg.url || firstImg.previewUrl || firstImg.preview_url || '';
        }
      }
    }

    // Double-check if imageUrl is still empty and videoUrl is set, try to find a preview in images array
    if (!imageUrl && videoUrl) {
      if (giveaway.images && Array.isArray(giveaway.images)) {
        for (const img of giveaway.images) {
          if (img.previewUrl || img.preview_url) {
            imageUrl = img.previewUrl || img.preview_url;
            break;
          }
        }
      }
    }

    // Clean up if videoUrl was set to a normal image extension
    if (videoUrl && videoUrl.match(/\.(jpg|jpeg|png|gif|webp)/i)) {
      if (!imageUrl) {
        imageUrl = videoUrl;
      }
      videoUrl = '';
    }

    return { imageUrl, videoUrl };
  }

  /**
   * Helper to generate HTML for rendering media, supporting direct MP4 videos, Vimeo embeds, and YouTube embeds
   */
  getMediaHtml(imageUrl, videoUrl, title, isHoverScale = true, lazyLoad = false) {
    const hoverClass = isHoverScale ? 'transition-transform duration-700 group-hover:scale-105' : '';
    
    if (videoUrl) {
      // 1. Check if Vimeo
      const vimeoMatch = videoUrl.match(/(?:video\/|vimeo\.com\/)(?:manage\/videos\/)?(\d+)/i);
      if (vimeoMatch) {
        const vimeoId = vimeoMatch[1];
        const embedUrl = `https://player.vimeo.com/video/${vimeoId}?autoplay=1&muted=1&loop=1&background=1&playsinline=1`;
        return `
          <div class="absolute inset-0 w-full h-full overflow-hidden pointer-events-none">
            <iframe ${lazyLoad ? 'src="about:blank"' : `src="${embedUrl}"`}
                    data-src="${embedUrl}"
                    class="absolute top-1/2 left-1/2 min-w-full min-h-full w-[115%] h-[115%] -translate-x-1/2 -translate-y-1/2 object-cover brightness-110 pointer-events-none scale-[1.15]"
                    frameborder="0"
                    allow="autoplay; fullscreen; picture-in-picture"
                    allowfullscreen
                    title="${title}"></iframe>
          </div>
        `;
      }

      // 2. Check if YouTube
      const ytMatch = videoUrl.match(/(?:embed\/|watch\?v=|youtu\.be\/|youtube\.com\/shorts\/|v\/|vi\/)?([a-zA-Z0-9_-]{11})/i);
      if (ytMatch) {
        const ytId = ytMatch[1];
        const embedUrl = `https://www.youtube.com/embed/${ytId}?autoplay=1&mute=1&loop=1&playlist=${ytId}&controls=0&showinfo=0&rel=0&playsinline=1&enablejsapi=1`;
        return `
          <div class="absolute inset-0 w-full h-full overflow-hidden pointer-events-none">
            <iframe ${lazyLoad ? 'src="about:blank"' : `src="${embedUrl}"`}
                    data-src="${embedUrl}"
                    class="absolute top-1/2 left-1/2 min-w-full min-h-full w-[115%] h-[115%] -translate-x-1/2 -translate-y-1/2 object-cover brightness-110 pointer-events-none scale-[1.15]"
                    frameborder="0"
                    allow="autoplay; encrypted-media; gyroscope; picture-in-picture"
                    allowfullscreen
                    title="${title}"></iframe>
          </div>
        `;
      }

      // 3. Fallback to standard HTML5 video tag for direct MP4 URLs
      return `
        <video autoplay muted loop playsinline class="absolute inset-0 w-full h-full object-cover brightness-110 ${hoverClass}" poster="${imageUrl}">
          <source src="${videoUrl}" type="video/mp4">
        </video>
      `;
    }

    if (imageUrl) {
      return `
        <img src="${imageUrl}" class="absolute inset-0 w-full h-full object-cover brightness-110 ${hoverClass}" loading="eager" alt="${title}">
      `;
    }

    return `
      <div class="absolute inset-0 bg-gray-950 flex items-center justify-center">
        <span class="text-gray-700 font-mono">MEDIA_PROTOCOL_OFFLINE</span>
      </div>
    `;
  }

  /**
   * Helper to get active bonus period end date from a giveaway object
   */
  getBonusEndDate(giveaway) {
    if (!giveaway || !giveaway.bonusPeriods || !Array.isArray(giveaway.bonusPeriods)) return '';
    const now = new Date();
    const activeBonus = giveaway.bonusPeriods.find(bp => {
      const start = new Date(bp.bonusStartDate);
      const end = new Date(bp.bonusEndDate);
      return now >= start && now <= end;
    });
    return activeBonus ? activeBonus.bonusEndDate : '';
  }

  /**
   * Helper to get initial bonus promo text based on end date
   */
  getBonusText(bonusEndDate) {
    if (!bonusEndDate) return 'ENTRIES PER $1 SPENT';
    const distance = new Date(bonusEndDate).getTime() - new Date().getTime();
    if (distance < 0) return 'ENTRIES PER $1 SPENT';
    const days = Math.floor(distance / (1000 * 60 * 60 * 24));
    const hours = Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const dayText = days === 1 ? 'day' : 'days';
    const hourText = hours === 1 ? 'hour' : 'hours';
    return `Ending in ${days} ${dayText} and ${hours} ${hourText}`;
  }

  /**
   * Main render routers based on giveaway count
   */
  renderGiveaways(giveaways, multiplier) {
    if (!giveaways || giveaways.length === 0) return;

    if (giveaways.length === 1) {
      this.renderSingleGiveaway(giveaways[0], multiplier);
    } else {
      this.renderSlideshow(giveaways, multiplier);
    }
  }

  /**
   * Render single active giveaway
   */
  renderSingleGiveaway(giveaway, multiplier) {
    const { imageUrl, videoUrl } = this.getMedia(giveaway);
    const title = giveaway.title || 'Exclusive Giveaway';
    const prizeInfo = giveaway.prize_info || giveaway.specs || giveaway.description || 'SUPERCHARGED PERFORMANCE';
    
    let endDate = giveaway.end_ts || '2026-12-31T23:59:59.000Z';
    if (giveaway.timeRemaining && typeof giveaway.timeRemaining.hours === 'number') {
      const hours = giveaway.timeRemaining.hours || 0;
      const minutes = giveaway.timeRemaining.minutes || 0;
      const seconds = giveaway.timeRemaining.seconds || 0;
      const totalMs = ((hours * 3600) + (minutes * 60) + seconds) * 1000;
      if (!isNaN(totalMs) && totalMs >= 0 && totalMs < 100 * 365 * 24 * 3600 * 1000) {
        try {
          endDate = new Date(Date.now() + totalMs).toISOString();
        } catch (err) {
          console.error('[GiveawayConsole] Failed to generate endDate string:', err);
        }
      }
    }

    const mediaHtml = this.getMediaHtml(imageUrl, videoUrl, title, true, false);

    const bonusEndDate = this.getBonusEndDate(giveaway);

    this.container.innerHTML = `
      ${mediaHtml}
      <!-- Overlay Gradients -->
      <div class="absolute inset-0 bg-gradient-to-t from-black via-black/20 to-black/40"></div>

      <!-- Flash Promo Overlay -->
      <div class="absolute top-6 left-0 w-full z-10 flex justify-center">
        <div class="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-gradient-to-r from-[#c70e28] to-[#a00820] shadow-[0_4px_20px_rgba(199,14,40,0.4)] animate-bounce">
          <span class="text-lg">
            <svg class="w-5 h-5 fill-white" viewBox="0 0 24 24"><path d="M7 2v11h3v9l7-12h-4l4-8z"/></svg>
          </span>
          <span class="text-base md:text-xl font-black text-white uppercase tracking-wider">${multiplier}X</span>
          <span class="text-white/50 text-xl">•</span>
          <flash-timer data-bonus-end-date="${bonusEndDate}">
            <span class="text-xs md:text-sm font-bold text-[#ffeb3b] font-mono tracking-tight" ref="timer">
              ${this.getBonusText(bonusEndDate)}
            </span>
          </flash-timer>
        </div>
      </div>

      <!-- Hero Content Bottom -->
      <div class="absolute bottom-8 left-8 right-8 z-10 flex flex-col md:flex-row md:items-end justify-between gap-6">
        <div class="max-w-2xl">
          <h3 class="text-2xl md:text-5xl font-black uppercase italic tracking-tighter text-white drop-shadow-[0_4px_12px_rgba(0,0,0,0.8)] leading-none mb-4">
            ${title}
          </h3>
          <div class="flex flex-wrap gap-3">
            ${prizeInfo ? `<span class="badge-spec uppercase tracking-widest text-[9px] md:text-[10px]">GIVEAWAY PRIZE: ${prizeInfo}</span>` : ''}
            <span class="badge-promo uppercase tracking-widest text-[9px] md:text-[10px] italic">PROMO: ACTIVE</span>
          </div>
        </div>

        <!-- Countdown Box -->
        <div class="countdown-box-container bg-black/60 backdrop-blur-xl border border-white/10 p-4 min-w-[180px] rounded-sm" style="display: none;">
          <p class="accent-text text-[9px] mb-1 leading-none font-black">GIVEAWAY COUNTDOWN</p>
          <giveaway-countdown data-end-date="${endDate}">
            <p class="text-xl md:text-2xl font-black font-mono text-white tracking-tighter tabular-nums" ref="timer">
              --:--:--
            </p>
          </giveaway-countdown>
          <div class="w-full bg-white/10 h-1 mt-3 overflow-hidden rounded-full">
            <div class="bg-accent h-full w-[65%] shadow-[0_0_12px_rgb(var(--color-accent-rgb))]"></div>
          </div>
        </div>
      </div>
    `;
  }

  /**
   * Render dynamic cross-fading slideshow
   */
  renderSlideshow(giveaways, multiplier) {
    let slidesHtml = '';
    let dotsHtml = '';

    giveaways.forEach((giveaway, index) => {
      const { imageUrl, videoUrl } = this.getMedia(giveaway);
      const title = giveaway.title || 'Exclusive Giveaway';
      const prizeInfo = giveaway.prize_info || giveaway.specs || giveaway.description || 'SUPERCHARGED PERFORMANCE';
      const bonusEndDate = this.getBonusEndDate(giveaway);
      
      let endDate = giveaway.end_ts || '2026-12-31T23:59:59.000Z';
      if (giveaway.timeRemaining && typeof giveaway.timeRemaining.hours === 'number') {
        const hours = giveaway.timeRemaining.hours || 0;
        const minutes = giveaway.timeRemaining.minutes || 0;
        const seconds = giveaway.timeRemaining.seconds || 0;
        const totalMs = ((hours * 3600) + (minutes * 60) + seconds) * 1000;
        if (!isNaN(totalMs) && totalMs >= 0 && totalMs < 100 * 365 * 24 * 3600 * 1000) {
          try {
            endDate = new Date(Date.now() + totalMs).toISOString();
          } catch (err) {
            console.error('[GiveawayConsole] Failed to generate endDate string:', err);
          }
        }
      }
      const isActive = index === 0;

      const mediaHtml = this.getMediaHtml(imageUrl, videoUrl, title, false, !isActive);

      slidesHtml += `
        <div class="giveaway-slide absolute inset-0 w-full h-full transition-opacity duration-1000 ease-in-out ${isActive ? 'opacity-100 z-10' : 'opacity-0 z-0 pointer-events-none'}" 
             role="group" 
             aria-roledescription="slide" 
             aria-label="Slide ${index + 1} of ${giveaways.length}"
             aria-hidden="${isActive ? 'false' : 'true'}"
             data-slide-index="${index}">
          
          ${mediaHtml}
          <!-- Overlay Gradients -->
          <div class="absolute inset-0 bg-gradient-to-t from-black via-black/20 to-black/40"></div>

          <!-- Flash Promo Overlay -->
          <div class="absolute top-6 left-0 w-full z-20 flex justify-center">
            <div class="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-gradient-to-r from-[#c70e28] to-[#a00820] shadow-[0_4px_20px_rgba(199,14,40,0.4)] animate-bounce">
              <span class="text-lg">
                <svg class="w-5 h-5 fill-white" viewBox="0 0 24 24"><path d="M7 2v11h3v9l7-12h-4l4-8z"/></svg>
              </span>
              <span class="text-base md:text-xl font-black text-white uppercase tracking-wider">${multiplier}X</span>
              <span class="text-white/50 text-xl">•</span>
              <flash-timer data-bonus-end-date="${bonusEndDate}">
                <span class="text-xs md:text-sm font-bold text-[#ffeb3b] font-mono tracking-tight" ref="timer">
                  ${this.getBonusText(bonusEndDate)}
                </span>
              </flash-timer>
            </div>
          </div>

          <!-- Hero Content Bottom -->
          <div class="absolute bottom-8 left-8 right-8 z-20 flex flex-col md:flex-row md:items-end justify-between gap-6">
            <div class="max-w-2xl">
              <h3 class="text-2xl md:text-5xl font-black uppercase italic tracking-tighter text-white drop-shadow-[0_4px_12px_rgba(0,0,0,0.8)] leading-none mb-4">
                ${title}
              </h3>
              <div class="flex flex-wrap gap-3">
                ${prizeInfo ? `<span class="badge-spec uppercase tracking-widest text-[9px] md:text-[10px]">GIVEAWAY PRIZE: ${prizeInfo}</span>` : ''}
                <span class="badge-promo uppercase tracking-widest text-[9px] md:text-[10px] italic">PROMO: ACTIVE</span>
              </div>
            </div>

            <!-- Countdown Box -->
            <div class="countdown-box-container bg-black/60 backdrop-blur-xl border border-white/10 p-4 min-w-[180px] rounded-sm" style="display: none;">
              <p class="accent-text text-[9px] mb-1 leading-none font-black">GIVEAWAY COUNTDOWN</p>
              <giveaway-countdown data-end-date="${endDate}">
                <p class="text-xl md:text-2xl font-black font-mono text-white tracking-tighter tabular-nums" ref="timer">
                  --:--:--
                </p>
              </giveaway-countdown>
              <div class="w-full bg-white/10 h-1 mt-3 overflow-hidden rounded-full">
                <div class="bg-accent h-full w-[65%] shadow-[0_0_12px_rgb(var(--color-accent-rgb))]"></div>
              </div>
            </div>
          </div>
        </div>
      `;

      dotsHtml += `
        <button class="w-8 h-1.5 transition-all duration-300 rounded-full ${isActive ? 'bg-accent shadow-[0_0_8px_var(--color-accent)]' : 'bg-white/30 hover:bg-white/60'}" 
                aria-label="Go to slide ${index + 1}"
                aria-current="${isActive ? 'true' : 'false'}"
                data-dot-index="${index}"></button>
      `;
    });

    const arrowsHtml = `
      <!-- Previous Arrow -->
      <button class="slideshow-arrow-btn absolute left-4 top-1/2 -translate-y-1/2 z-30 w-10 h-10 flex items-center justify-center bg-black/40 border border-white/10 text-white hover:bg-accent hover:text-black hover:border-accent transition-all duration-300 rounded-full backdrop-blur-sm" 
              aria-label="Previous slide" 
              id="slideshow-prev-btn">
        <svg class="w-5 h-5 fill-current" viewBox="0 0 24 24"><path d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z"/></svg>
      </button>
      <!-- Next Arrow -->
      <button class="slideshow-arrow-btn absolute right-4 top-1/2 -translate-y-1/2 z-30 w-10 h-10 flex items-center justify-center bg-black/40 border border-white/10 text-white hover:bg-accent hover:text-black hover:border-accent transition-all duration-300 rounded-full backdrop-blur-sm" 
              aria-label="Next slide" 
              id="slideshow-next-btn">
        <svg class="w-5 h-5 fill-current" viewBox="0 0 24 24"><path d="M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z"/></svg>
      </button>
    `;

    const controlsContainerHtml = `
      <div class="absolute bottom-4 left-1/2 -translate-x-1/2 z-30 flex gap-2">
        ${dotsHtml}
      </div>
    `;

    // Overwrite the existing content with the newly generated slideshow structure
    this.container.innerHTML = `
      <div class="relative w-full h-full min-h-[400px] lg:min-h-[600px] overflow-hidden" 
           role="region" 
           aria-roledescription="carousel" 
           aria-label="Active Giveaways" 
           aria-live="off" 
           id="giveaway-slideshow-inner">
        ${slidesHtml}
        ${arrowsHtml}
        ${controlsContainerHtml}
      </div>
    `;

    this.initSlideshowLogic(giveaways.length);
  }

  /**
   * Binds event listeners and states to the slideshow elements
   */
  initSlideshowLogic(slidesCount) {
    const innerContainer = this.querySelector('#giveaway-slideshow-inner');
    this.slides = Array.from(this.querySelectorAll('[data-slide-index]'));
    this.dots = Array.from(this.querySelectorAll('[data-dot-index]'));
    const prevBtn = this.querySelector('#slideshow-prev-btn');
    const nextBtn = this.querySelector('#slideshow-next-btn');

    if (!innerContainer || this.slides.length === 0) return;

    this.currentIndex = 0;

    const showSlide = (index) => {
      // Loop boundaries
      if (index >= slidesCount) index = 0;
      if (index < 0) index = slidesCount - 1;

      this.currentIndex = index;

      this.slides.forEach((slide, idx) => {
        const isActive = idx === this.currentIndex;
        slide.classList.toggle('opacity-100', isActive);
        slide.classList.toggle('opacity-0', !isActive);
        slide.classList.toggle('z-10', isActive);
        slide.classList.toggle('z-0', !isActive);
        slide.classList.toggle('pointer-events-auto', isActive);
        slide.classList.toggle('pointer-events-none', !isActive);
        slide.setAttribute('aria-hidden', !isActive ? 'true' : 'false');

        // Play video in active slide; pause and reset video in inactive slides
        const video = slide.querySelector('video');
        if (video) {
          if (isActive) {
            video.play().catch(err => console.warn('[GiveawayConsole] Failed to play video:', err));
          } else {
            video.pause();
            video.currentTime = 0;
          }
        }

        // Handle iframe video (Vimeo/YouTube)
        const iframe = slide.querySelector('iframe');
        if (iframe) {
          if (isActive) {
            const targetSrc = iframe.getAttribute('data-src');
            if (targetSrc && iframe.getAttribute('src') !== targetSrc) {
              iframe.setAttribute('src', targetSrc);
            }
          } else {
            iframe.setAttribute('src', 'about:blank');
          }
        }
      });

      this.dots.forEach((dot, idx) => {
        const isActive = idx === this.currentIndex;
        dot.classList.toggle('bg-accent', isActive);
        dot.classList.toggle('shadow-[0_0_8px_var(--color-accent)]', isActive);
        dot.classList.toggle('bg-white/30', !isActive);
        dot.classList.toggle('hover:bg-white/60', !isActive);
        dot.setAttribute('aria-current', isActive ? 'true' : 'false');
      });
    };

    const nextSlide = () => showSlide(this.currentIndex + 1);
    const prevSlide = () => showSlide(this.currentIndex - 1);

    this.startAutoplay = () => {
      this.stopAutoplay();
      innerContainer.setAttribute('aria-live', 'off');
      this.autoplayTimer = setInterval(nextSlide, this.autoplayDelay);
    };

    this.stopAutoplay = () => {
      if (this.autoplayTimer) {
        clearInterval(this.autoplayTimer);
        this.autoplayTimer = null;
      }
      innerContainer.setAttribute('aria-live', 'polite');
    };

    // Navigation triggers
    if (prevBtn) {
      prevBtn.addEventListener('click', () => {
        prevSlide();
        this.startAutoplay();
      });
    }

    if (nextBtn) {
      nextBtn.addEventListener('click', () => {
        nextSlide();
        this.startAutoplay();
      });
    }

    this.dots.forEach(dot => {
      dot.addEventListener('click', () => {
        const idx = parseInt(dot.getAttribute('data-dot-index'));
        showSlide(idx);
        this.startAutoplay();
      });
    });

    // Keyboard controls
    innerContainer.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowLeft') {
        prevSlide();
        this.startAutoplay();
      } else if (e.key === 'ArrowRight') {
        nextSlide();
        this.startAutoplay();
      }
    });

    // Hover & focus handlers
    innerContainer.addEventListener('mouseenter', () => this.stopAutoplay());
    innerContainer.addEventListener('mouseleave', () => this.startAutoplay());
    innerContainer.addEventListener('focusin', () => this.stopAutoplay());
    innerContainer.addEventListener('focusout', () => this.startAutoplay());

    // Start auto cycle with initial slide state explicitly initialized
    showSlide(0);
    this.startAutoplay();
  }
}

if (!customElements.get('giveaway-console')) {
  customElements.define('giveaway-console', GiveawayConsole);
}
